import { BaseMeiucaService } from './MeiucaService'
import { env } from '@/lib/env'
import type { VenueDetailResponse } from '../types/sdui-arena-detail'

export interface ArenaSuggestionData {
  type: 'google_place' | 'manual'
  name: string
  address?: string
  city?: string
  state?: string
  googlePlaceId?: string | null
  coordinate?: {
    latitude: number
    longitude: number
  }
}

interface ArenaSuggestionResponse {
  id: string
  type: 'google_place' | 'manual'
  name: string
  address?: string
  city?: string
  state?: string
  googlePlaceId?: string | null
  latitude?: number
  longitude?: number
  createdAt?: string
  updatedAt?: string
}

export interface VenueSearchItem {
  id: string
  name: string
  slug?: string
  address?: string
  city?: string
  state?: string
  logoUrl?: string
  coverUrl?: string
  latitude?: number
  longitude?: number
  distance?: number
}

export interface VenueSearchParams {
  query?: string
  lat?: number
  lng?: number
  radius?: number
  modalities?: string[]
  amenities?: string[]
}

export interface VenueSearchResponse {
  venues: VenueSearchItem[]
  total: number
}

class ArenaService extends BaseMeiucaService {
  /**
   * Search venues/arenas
   */
  async searchVenues(params: VenueSearchParams): Promise<VenueSearchResponse> {
    console.log('[arenaService] searchVenues called with params:', params)

    const queryParams = new URLSearchParams()

    if (params.query) queryParams.append('q', params.query)
    if (params.lat !== undefined) queryParams.append('lat', params.lat.toString())
    if (params.lng !== undefined) queryParams.append('lng', params.lng.toString())
    if (params.radius !== undefined) queryParams.append('radius', params.radius.toString())
    if (params.modalities && params.modalities.length > 0) {
      params.modalities.forEach(modality => queryParams.append('modalities', modality))
    }
    if (params.amenities && params.amenities.length > 0) {
      params.amenities.forEach(amenity => queryParams.append('amenities', amenity))
    }

    const queryString = queryParams.toString()
    const url = queryString ? `/venues?${queryString}` : '/venues'

    console.log('[arenaService] URL constructed:', url)
    console.log('[arenaService] Calling GET request...')

    const response = await this.get<{ data: VenueSearchResponse }>(url)

    console.log('[arenaService] Response received:', response)

    return response.data.data
  }

  /**
   * Get the structured venue/arena detail payload. The client composes the sections.
   */
  async getVenueDetail(venueId: string): Promise<VenueDetailResponse> {
    const response = await this.get<{ data: VenueDetailResponse }>(`/venues/${venueId}`)
    return response.data.data
  }

  /**
   * Submit a suggestion for a new arena
   */
  async suggestArena(data: ArenaSuggestionData): Promise<ArenaSuggestionResponse> {
    const payload = {
      type: data.type,
      name: data.name,
      ...(data.type === 'google_place' && {
        address: data.address,
        google_place_id: data.googlePlaceId,
        latitude: data.coordinate?.latitude,
        longitude: data.coordinate?.longitude,
      }),
      ...(data.type === 'manual' && {
        city: data.city,
        state: data.state,
      }),
    }

    const response = await this.post<ArenaSuggestionResponse>('/support/suggestions/venues', payload)
    return response.data
  }

}

export const arenaService = new ArenaService({
  baseURL: env.API_URL,
  refreshEndpoint: '/v1/auth/refresh',
  cookiePrefix: '',
  debug: env.IS_DEVELOPMENT,
})
